import dayjs from 'dayjs';

import { useCallback, useEffect, useMemo, useState } from 'react';

import ChartHeader from 'components/charts/ChartHeader';
import DataCards from 'components/charts/DataCards';
import { DynamicListChartContainer } from 'components/charts/styled/Chart.styled';
import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import DateInput from 'components/field/DateInput';
import EnumSelect from 'components/field/EnumSelect';
import Card from 'components/global/Card';
import Popper from 'components/global/Popper';
import Typography from 'components/global/Typography';
import DynamicList from 'components/list/DynamicList';
import { FilterAction, FilterType, ListComponents } from 'components/list/DynamicListFilterUtils';
import DynamicListStringInput from 'components/list/FilterInputs/DynamicListStringInput';

import useFetchAndParseOnView from 'hooks/useFetchAndParseOnView';

import { AUTOCOMPLETE, ENUMS, REPORT } from 'services/consts';

import { DYNAMICLISTS } from 'utils/constants';

import * as S from './AggregatorDCPerformance.styled';

const FILTERS = {
	contactName: 'contactName',
	shipmentCreatedDate: 'shipmentCreatedDate',
	serviceType: 'serviceType',
	clientName: 'clientName',
	clientCountry: 'clientCountry',
	maxTier: 'maxTier',
	agreementType: 'agreementType',
	originCountry: 'originCountry',
	originCity: 'originCity',
	destinationCountry: 'destinationCountry',
	destinationCity: 'destinationCity'
};

const FILTER_LIST = {
	[FILTERS.shipmentCreatedDate]: {
		key: [FILTERS.shipmentCreatedDate],
		action: FilterAction.between,
		filterType: FilterType.date,
		from: dayjs().subtract(29, 'day').startOf('day').toISOString(),
		to: dayjs().endOf('day').toISOString()
	},
	[FILTERS.serviceType]: {
		key: [FILTERS.serviceType],
		action: FilterAction.is,
		filterType: FilterType.multiSelection,
		value: []
	},
	[FILTERS.contactName]: {
		key: [FILTERS.contactName],
		action: FilterAction.is,
		filterType: FilterType.string,
		value: []
	},
	[FILTERS.clientName]: {
		key: [FILTERS.clientName],
		action: FilterAction.is,
		filterType: FilterType.enumMulti,
		value: []
	},
	[FILTERS.clientCountry]: {
		key: [FILTERS.clientCountry],
		action: FilterAction.is,
		filterType: FilterType.enum,
		value: []
	},
	[FILTERS.destinationCountry]: {
		key: [FILTERS.destinationCountry],
		action: FilterAction.is,
		filterType: FilterType.enum,
		value: []
	},
	[FILTERS.originCountry]: {
		key: [FILTERS.originCountry],
		action: FilterAction.is,
		filterType: FilterType.enum,
		value: []
	},
	[FILTERS.originCity]: {
		key: [FILTERS.originCity],
		action: FilterAction.is,
		filterType: FilterType.enum,
		value: []
	},
	[FILTERS.destinationCity]: {
		key: [FILTERS.destinationCity],
		action: FilterAction.is,
		filterType: FilterType.enum,
		value: []
	},
	[FILTERS.maxTier]: {
		key: [FILTERS.maxTier],
		action: FilterAction.is,
		filterType: FilterType.enum,
		value: []
	},
	[FILTERS.agreementType]: {
		key: [FILTERS.agreementType],
		action: FilterAction.is,
		filterType: FilterType.enum,
		value: []
	}
};

const Filter = ({ filter, changeFilter }) => {
	const [filters, setFilters] = useState([]);
	const [filtersMenuButtonRef, setFilterMenuButtonRef] = useState(null);
	const getSingleFilter = useCallback((key) => filters?.find((f) => f.key[0] === key), [filters]);

	const applyAllFilters = (closePopper) => {
		changeFilter(filters);
		closePopper?.();
	};

	useEffect(() => {
		setFilters(filter);
	}, [filter]);

	const addFilter = (key, value, type = 'string') => {
		setFilters((prev) => {
			const newFilters = [...prev];
			const filterIndex = newFilters.findIndex((f) => f.key[0] === key);
			if (filterIndex === -1) {
				newFilters.push({
					...FILTER_LIST[key],
					...(type === 'string' ? { value } : { to: value.to, from: value.from })
				});
			} else {
				newFilters[filterIndex] = {
					...newFilters[filterIndex],
					...(type === 'string' ? { value } : { to: value.to, from: value.from })
				};
			}
			return newFilters;
		});
	};

	return (
		<ChartHeader
			title="totalNumbers"
			filter={
				<>
					<Button
						id="filters-button"
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/filter-icon.json.gz"
						ref={setFilterMenuButtonRef}
						variant="borderless"
						label="allFilters"
					/>

					{filtersMenuButtonRef && (
						<Popper
							refElement={filtersMenuButtonRef}
							placement="bottom-end"
							onClose={() => setFilters(filter)}>
							{(closePopper) => (
								<S.PopperMenu>
									<S.PopperMenuItem>
										<Typography variant="p2" fontWeight="500">
											shipmentCreatedDate
										</Typography>
										<DateInput
											value={{
												start: getSingleFilter(FILTERS.shipmentCreatedDate)?.from,
												end: getSingleFilter(FILTERS.shipmentCreatedDate)?.to
											}}
											setValue={(date) =>
												addFilter(
													FILTERS.shipmentCreatedDate,
													{ from: date?.start, to: date?.end },
													'date'
												)
											}
											rangeMode
										/>
									</S.PopperMenuItem>

									<S.PopperMenuItem>
										<Typography variant="p2" fontWeight="500">
											clientName
										</Typography>
										<AutoComplete
											autoCompleteName={AUTOCOMPLETE.CLIENTS}
											value={getSingleFilter(FILTERS.clientName)?.value}
											setValue={(value) => {
												addFilter(
													FILTERS.clientName,
													value ? value.map((item) => item.ID) : null
												);
											}}
											columns={[
												{ accessor: 'ID', width: 100 },
												{ accessor: 'Name', width: 400 }
											]}
											selectionLabelAccessor={['ID', 'Name']}
											multipleSelection
											fixedContainerWidth={false}
										/>
									</S.PopperMenuItem>

									<S.PopperMenuItem>
										<Typography variant="p2" fontWeight="500">
											maxTier
										</Typography>
										<EnumSelect
											enumName={ENUMS.DC_CITY_TIER}
											value={getSingleFilter(FILTERS.maxTier)?.value}
											setValue={(value) => addFilter(FILTERS.maxTier, value ? [value] : null)}
										/>
									</S.PopperMenuItem>

									<S.PopperMenuItem>
										<Typography variant="p2" fontWeight="500">
											agreementType
										</Typography>
										<EnumSelect
											data={['AGGREGATOR', 'OMNI_AGGREGATOR']}
											value={getSingleFilter(FILTERS.agreementType)?.value}
											setValue={(value) =>
												addFilter(FILTERS.agreementType, value ? [value] : null)
											}
										/>
									</S.PopperMenuItem>
									<S.PopperMenuItem>
										<Typography variant="p2" fontWeight="500">
											serviceType
										</Typography>
										<EnumSelect
											enumName={ENUMS.PRICE_CONFIGURATION_SERVICE_TYPE}
											value={getSingleFilter(FILTERS.serviceType)?.value || []}
											multipleSelection
											setValue={(value) => addFilter(FILTERS.serviceType, value ? value : null)}
										/>
									</S.PopperMenuItem>
									{Object.keys(FILTERS).map((key) => {
										const filter = FILTER_LIST[key];
										if (filter?.filterType === FilterType.string) {
											return (
												<S.PopperMenuItem key={filter.key[0]}>
													<Typography variant="p2" fontWeight="500">
														{filter.key[0]}
													</Typography>
													<DynamicListStringInput
														value={getSingleFilter(filter.key[0])?.value}
														setValue={(value) =>
															addFilter(filter.key[0], value ? [value] : null)
														}
														autoFocus={false}
														style={{ padding: 0 }}
													/>
												</S.PopperMenuItem>
											);
										}
									})}
									<S.PopperMenuItem>
										<Typography variant="p2" fontWeight="500">
											originCountry
										</Typography>
										<EnumSelect
											enumName={ENUMS.COUNTRIES}
											value={getSingleFilter(FILTERS.originCountry)?.value}
											setValue={(value) =>
												addFilter(FILTERS.originCountry, value ? [value] : null)
											}
											labelPrefix="country."
										/>
									</S.PopperMenuItem>
									<S.PopperMenuItem>
										<Typography variant="p2" fontWeight="500">
											originCity
										</Typography>
										<AutoComplete
											autoCompleteName={AUTOCOMPLETE.CITIES_LIST}
											value={getSingleFilter(FILTERS.originCity)?.value?.toString()}
											setValue={(value) => {
												addFilter(FILTERS.originCity, value?.ID ? [value.ID] : null);
											}}
											selectionLabelAccessor={['name', 'country']}
											columns={[
												{ accessor: 'name', width: 200 },
												{ accessor: 'country', width: 100 }
											]}
										/>
									</S.PopperMenuItem>
									<S.PopperMenuItem>
										<Typography variant="p2" fontWeight="500">
											destinationCountry
										</Typography>
										<EnumSelect
											enumName={ENUMS.COUNTRIES}
											value={getSingleFilter(FILTERS.destinationCountry)?.value}
											setValue={(value) =>
												addFilter(FILTERS.destinationCountry, value ? [value] : null)
											}
											labelPrefix="country."
										/>
									</S.PopperMenuItem>
									<S.PopperMenuItem>
										<Typography variant="p2" fontWeight="500">
											destinationCity
										</Typography>
										<AutoComplete
											autoCompleteName={AUTOCOMPLETE.CITIES_LIST}
											value={getSingleFilter(FILTERS.destinationCity)?.value?.toString()}
											setValue={(value) => {
												addFilter(FILTERS.destinationCity, value?.ID ? [value.ID] : null);
											}}
											selectionLabelAccessor={['name', 'country']}
											columns={[
												{ accessor: 'name', width: 200 },
												{ accessor: 'country', width: 100 }
											]}
										/>
									</S.PopperMenuItem>
									<S.PopperMenuItem>
										<Typography variant="p2" fontWeight="500">
											clientCountry
										</Typography>
										<EnumSelect
											enumName={ENUMS.COUNTRIES}
											value={getSingleFilter(FILTERS.clientCountry)?.value}
											setValue={(value) =>
												addFilter(FILTERS.clientCountry, value ? [value] : null)
											}
											labelPrefix="country."
										/>
									</S.PopperMenuItem>
									<S.PopperMenuItem className="sticky">
										<Button
											variant="action"
											label="applyFilter"
											size="small"
											onClick={() => applyAllFilters(closePopper)}
										/>
									</S.PopperMenuItem>
								</S.PopperMenu>
							)}
						</Popper>
					)}
				</>
			}></ChartHeader>
	);
};

const ScoresOverview = ({ filter }) => {
	const cardsConfig = [
		{
			accessor: 'deliveredPercentage',
			label: 'deliveredPercentage',
			customIcon: 'fa-regular-box-circle-check',
			backgroundColor: 'var(--fa-regular-box-circle-check-bgColor)',
			color: 'var(--fa-regular-box-circle-check-color)'
		},
		{
			accessor: 'deliverySlaSuccessPercentage',
			label: 'deliverySlaSuccessPercentage',
			customIcon: 'fa-regular-box-circle-check',
			backgroundColor: 'var(--fa-regular-box-circle-check-bgColor)',
			color: 'var(--fa-regular-box-circle-check-color)'
		},
		{
			accessor: 'notLostPercentage',
			label: 'notLostPercentage',
			customIcon: 'fa-regular-box-circle-xmark',
			backgroundColor: 'var(--fa-regular-box-circle-xmark-bgColor)',
			color: 'var(--fa-regular-box-circle-xmark-color)'
		},
		{
			accessor: 'pickedUpPercentage',
			label: 'pickedUpPercentage',
			customIcon: 'fas fa-person-carry-box',
			backgroundColor: 'var(--fa-person-carry-box-bgColor)',
			color: 'var(--fa-person-carry-box-color)'
		},
		{
			accessor: 'pickupSlaSuccessPercentage',
			label: 'pickupSlaSuccessPercentage',
			customIcon: 'fas fa-people-carry-box',
			backgroundColor: 'var(--fa-people-carry-box-bgColor)',
			color: 'var(--fa-people-carry-box-color)'
		},
		{
			accessor: 'score',
			label: 'score',
			customIcon: 'fas fa-party-horn',
			backgroundColor: 'var(--fa-party-horn-bgColor)',
			color: 'var(--fa-party-horn-color)'
		}
	];

	const body = useMemo(() => ({ filter, from: undefined, to: undefined }), [filter]);

	const { ref, data, loading } = useFetchAndParseOnView({
		url: REPORT.DC.GET_DC_SCORE_OVERVIEW,
		body
	});

	return (
		<div ref={ref}>
			<DataCards data={data} loading={loading} cardsConfig={cardsConfig} defaultValue={0} />
		</div>
	);
};

const AggregatorDCList = ({ filter }) => {
	const columns = useMemo(
		() => [
			{
				Header: 'deliveryCompanyName',
				accessor: 'deliveryCompanyName',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.DELIVERY_COMPANY_BY_NAME
			},
			{
				Header: 'serviceType',
				accessor: 'serviceType',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.PRICE_CONFIGURATION_SERVICE_TYPE
			},
			{
				Header: 'agreementType',
				accessor: 'agreementType',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.SHIPPING_PRICE_AGREEMENT_TYPE
			},
			{
				Header: 'integrationName',
				accessor: 'integrationName',
				filterType: FilterType.string
			},
			{
				Header: 'clientName',
				accessor: 'clientName',
				filterType: FilterType.multiSelection
			},
			{
				Header: 'dcPerformance.numberOfShipments',
				accessor: 'numberOfTotalOrders',
				filterType: FilterType.float
			},
			{
				Header: 'dcPerformance.numberOfPickedUpShipments',
				accessor: 'numberOfPickedUpShipments',
				filterType: FilterType.float
			},
			{
				Header: 'dcPerformance.numberOfPickupSlaSuccess',
				accessor: 'numberOfPickupSlaSuccess',
				filterType: FilterType.float
			},
			{
				Header: 'dcPerformance.pickedUpPercentage',
				accessor: 'pickedUpPercentage',
				filterType: FilterType.float,
				renderCell: ListComponents.PERCENTAGE
			},
			{
				Header: 'dcPerformance.pickupSlaSuccessPercentage',
				accessor: 'pickupSlaSuccessPercentage',
				filterType: FilterType.float,
				renderCell: ListComponents.PERCENTAGE
			},
			{
				Header: 'dcPerformance.numberOfDeliveredOrders',
				accessor: 'numberOfDeliveredOrders',
				filterType: FilterType.float
			},
			{
				Header: 'dcPerformance.numberOfDeliveredSlaSuccess',
				accessor: 'numberOfDeliveredSlaSuccess',
				filterType: FilterType.float
			},
			{
				Header: 'dcPerformance.deliveredPercentage',
				accessor: 'deliveredPercentage',
				filterType: FilterType.float,
				renderCell: ListComponents.PERCENTAGE
			},
			{
				Header: 'dcPerformance.deliverySlaSuccessPercentage',
				accessor: 'deliverySlaSuccessPercentage',
				filterType: FilterType.float,
				renderCell: ListComponents.PERCENTAGE
			},
			{
				Header: 'dcPerformance.numberOfLostShipments',
				accessor: 'numberOfLostShipments',
				filterType: FilterType.float
			},
			{
				Header: 'dcPerformance.notLostPercentage',
				accessor: 'notLostPercentage',
				filterType: FilterType.float,
				renderCell: ListComponents.PERCENTAGE
			},
			{
				Header: 'dcPerformance.numberOfPickedUpAll',
				accessor: 'numberOfPickedUpAll',
				filterType: FilterType.float
			},
			{
				Header: 'dcPerformance.numberOfPickedUpEligible',
				accessor: 'numberOfPickedUpEligible',
				filterType: FilterType.float
			},
			{
				Header: 'dcPerformance.numberOfPickupSlaEligible',
				accessor: 'numberOfPickupSlaEligible',
				filterType: FilterType.float
			},
			{
				Header: 'dcPerformance.numberOfDeliveredEligible',
				accessor: 'numberOfDeliveredEligible',
				filterType: FilterType.float
			},
			{
				Header: 'dcPerformance.numberOfDeliveredSlaEligible',
				accessor: 'numberOfDeliveredSlaEligible',
				filterType: FilterType.float
			},
			{
				Header: 'country',
				accessor: 'country',
				enumName: ENUMS.COUNTRIES,
				filterType: FilterType.enumMulti
			},
			{
				Header: 'dcPerformance.score',
				accessor: 'score',
				filterType: FilterType.float,
				renderCell: ListComponents.PERCENTAGE
			}
		],
		[]
	);

	return (
		<Card>
			<ChartHeader title="deliveryCompanyScoreList" />
			<DynamicListChartContainer>
				<DynamicList
					id={DYNAMICLISTS.DELIVERY_COMPANY_SCORE_LIST}
					columns={columns}
					defaultFilters={filter}
					fetchUrl={REPORT.DC.GET_DC_SCORE_LIST}
					exportName="deliveryCompanyScoreList"
					showColumnCalculation={false}
					showFilters={false}
				/>
			</DynamicListChartContainer>
		</Card>
	);
};

const AggregatorDCPerformance = () => {
	const [filterList, setFilterList] = useState(JSON.parse(JSON.stringify(FILTER_LIST)));

	const pageFilter = useMemo(() => {
		const result = [];
		Object.keys(filterList).forEach((key) => {
			const filterObj = filterList[key];
			if ((filterObj?.value?.length || 0) === 0 && !filterObj?.from) {
				return;
			}
			result.push(filterObj);
		});
		return result;
	}, [filterList]);

	const formatTableFilter = useCallback((filters) => {
		if (!Array.isArray(filters)) {
			return undefined;
		}
		const model = Object.freeze({
			column: {
				Header: '',
				accessor: '',
				filterType: '',
				enumName: '',
				filterRemovable: false,
				customFilterOptions: []
			},
			action: '',
			value: {}
		});
		const result = [];

		filters.forEach((filter) => {
			const { key, action, filterType, from, to, value } = filter;
			if (filterType === FilterType.date) {
				result.push({
					column: { ...model.column, Header: key[0], accessor: key[0], filterType },
					action,
					value: {
						start: from,
						end: to
					}
				});
			} else {
				result.push({
					column: { ...model.column, Header: key[0], accessor: key[0], filterType, enumName: '' },
					action,
					value: FilterType.multiSelection === filterType ? value : value.toString()
				});
			}
		});

		return result;
	}, []);

	return (
		<S.Container>
			<div className="row row-cols-1 mt-3">
				<Filter filter={pageFilter} changeFilter={setFilterList} />
				<ScoresOverview filter={pageFilter} />
			</div>
			<div className="row row-cols-1 mt-3">
				<AggregatorDCList filter={formatTableFilter(pageFilter)} />
			</div>
		</S.Container>
	);
};

export default AggregatorDCPerformance;
